/**
 * PlatformScopeSelector — topbar scope indicator.
 *   • super_admin  → 2 interactive dropdowns (Institution → Campus).
 *   • bound admin  → read-only badges showing their locked institution/campus.
 *   • everyone else → nothing.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlatformScope } from "@/context/PlatformScope";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Building2, MapPin, X, Lock } from "lucide-react";

const ALL_VALUE = "__ALL__";

export function PlatformScopeSelector() {
  const {
    isSuper,
    isBound,
    institutionId,
    campusId,
    setInstitutionId,
    setCampusId,
    clear,
  } = usePlatformScope();
  const { user } = useAuth();

  // Fetch institutions/campuses whenever we need to resolve a name — either
  // for the super-admin picker OR to label the bound admin's locked badge.
  const instsQ = useQuery({
    queryKey: ["scope-institutions"],
    queryFn: () => api.get("/institutions").then((r) => r.data),
    enabled: isSuper || isBound,
    staleTime: 5 * 60_000,
  });

  const campsQ = useQuery({
    queryKey: ["scope-campuses", institutionId],
    queryFn: () =>
      api
        .get("/campuses", { params: institutionId ? { institution_id: institutionId } : {} })
        .then((r) => r.data),
    enabled: (isSuper || isBound) && !!institutionId,
    staleTime: 5 * 60_000,
  });

  const insts = useMemo(() => instsQ.data || [], [instsQ.data]);
  const camps = useMemo(() => campsQ.data || [], [campsQ.data]);
  const hasScope = !!institutionId || !!campusId;

  if (!isSuper && !isBound) return null;

  // ─── Bound admin: read-only locked badge ───────────────────────────────
  if (isBound) {
    const inst = insts.find((i) => i.id === institutionId);
    const camp = camps.find((c) => c.id === campusId);
    // Prefer names hydrated on /auth/me (works for campus_admin who cannot list
    // /institutions or /campuses). Fall back to the list-query result, then to
    // a generic placeholder.
    const instLabel =
      user?.institution_name || inst?.name || (institutionId ? "Your institution" : null);
    const campLabel =
      user?.campus_name || camp?.name || (campusId ? "Your campus" : null);

    return (
      <div
        className="flex items-center gap-2"
        data-testid="platform-scope-selector"
        data-scope-mode="locked"
      >
        {instLabel && (
          <div
            className="flex items-center gap-1.5 h-9 px-3 rounded-md bg-muted/40 border border-border/70 text-[13px] max-w-[220px]"
            data-testid="scope-institution-locked"
            title={`Locked to ${instLabel}`}
          >
            <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="truncate">{instLabel}</span>
            <Lock className="h-3 w-3 text-muted-foreground/70 shrink-0" />
          </div>
        )}
        {campLabel && (
          <div
            className="flex items-center gap-1.5 h-9 px-3 rounded-md bg-muted/40 border border-border/70 text-[13px] max-w-[220px]"
            data-testid="scope-campus-locked"
            title={`Locked to ${campLabel}`}
          >
            <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="truncate">{campLabel}</span>
            <Lock className="h-3 w-3 text-muted-foreground/70 shrink-0" />
          </div>
        )}
      </div>
    );
  }

  // ─── Super admin: interactive picker ───────────────────────────────────
  return (
    <div className="flex items-center gap-2" data-testid="platform-scope-selector" data-scope-mode="interactive">
      {/* Institution */}
      <Select
        value={institutionId || ALL_VALUE}
        onValueChange={(v) => setInstitutionId(v === ALL_VALUE ? null : v)}
      >
        <SelectTrigger
          className="h-9 min-w-[180px] max-w-[220px] bg-muted/40 border-border/70 text-[13px]"
          data-testid="scope-institution-select"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <SelectValue placeholder="All institutes" />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE} data-testid="scope-institution-all">
            All institutes
          </SelectItem>
          {insts.map((i) => (
            <SelectItem key={i.id} value={i.id} data-testid={`scope-institution-${i.id}`}>
              {i.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Campus */}
      <Select
        value={campusId || ALL_VALUE}
        onValueChange={(v) => setCampusId(v === ALL_VALUE ? null : v)}
        disabled={!institutionId}
      >
        <SelectTrigger
          className="h-9 min-w-[160px] max-w-[220px] bg-muted/40 border-border/70 text-[13px]"
          data-testid="scope-campus-select"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <SelectValue placeholder={institutionId ? "All campuses" : "—"} />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE} data-testid="scope-campus-all">
            All campuses
          </SelectItem>
          {camps.map((c) => (
            <SelectItem key={c.id} value={c.id} data-testid={`scope-campus-${c.id}`}>
              {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasScope && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={clear}
          title="Clear scope"
          data-testid="scope-clear-btn"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
